import { UsePollerArgs, PollingSubscription } from '@/app/shared/types/stores/user-poll.state.interface';

const defaultPollingTimeInMS = 30000;

type PollerState = 'STARTED' | 'INITIATED' | 'STOPPED';
const usePoller = <T>(args: UsePollerArgs<T>) => {
  let pollerState: PollerState = 'INITIATED';
  const {
    pollingTimeInMS = defaultPollingTimeInMS,
    firstPollingTimeInMS = defaultPollingTimeInMS,
    pollingFunction,
    renewEarlyMS = 0,
    onPollingSuccess,
    onPollingError,
  } = args;

  let internalPollingTimeInMilliSeconds = pollingTimeInMS;

  let pollingTimeoutHandle: ReturnType<typeof setTimeout> | undefined;

  async function internalPollingFunction(): Promise<void> {
    try {
      const t = await pollingFunction();
      if (onPollingSuccess) {
        onPollingSuccess(t);
      }
    } catch (err) {
      if (onPollingError) {
        onPollingError(err);
      }
    }
  }

  const getNextScheduledTimeInMS = () => {
    return Math.max(
      0,
      internalPollingTimeInMilliSeconds - renewEarlyMS
    );
  };

  const schedulePollingFunction = (isFirstSchedule: boolean = false) => {
    if (isFirstSchedule || pollerState === 'STARTED') {
      pollingTimeoutHandle = setTimeout(
        async () =>
          internalPollingFunction()
            .then(() => (pollerState = 'STARTED'))
            .finally(() => {
              schedulePollingFunction(false);
            }),
        isFirstSchedule ? firstPollingTimeInMS : getNextScheduledTimeInMS()
      );
    }
  };

  const start = (): PollingSubscription => {
    if (pollerState === 'STOPPED') {
      throw new Error(
        `Poller is already stopped. You cannot start a stopped poller.`
      );
    }
    if (pollerState === 'STARTED') {
      throw new Error(
        `Poller has already started. You cannot start a start a started poller.`
      );
    }
    schedulePollingFunction(true);

    return {
      stop() {
        if (pollingTimeoutHandle) {
          clearTimeout(pollingTimeoutHandle);
        }
        pollerState = 'STOPPED';
      },
    };
  };
  return {
    start,
    setPollingTime: (newPollingTimeInSeconds: number) => {
      internalPollingTimeInMilliSeconds = newPollingTimeInSeconds;
    },
  };
};

export default usePoller;
